import { Layer } from "@/Utils/hierarchy";

import { MROIGridFilters } from "./constants/types";

enum FunnelStage {
  All = "all",
  Portfolio = "customer",
  Brand = "brand",
  Category = "category",
  Product = "product",
}

type FilterMap = {
  [key in Layer]: {
    [key in FunnelStage]?: MROIGridFilters[];
  };
};

// Common filter sets that are reused across different layers/stages
const UPPER_FUNNEL_FILTERS = [
  MROIGridFilters.UPPER_FUNNEL_SPEND_PERCENTAGE,
  MROIGridFilters.UPPER_FUNNEL_REVENUE_PERCENTAGE,
  MROIGridFilters.UPPER_FUNNEL_SPEND_AMOUNT,
  MROIGridFilters.UPPER_FUNNEL_REVENUE_AMOUNT,
  MROIGridFilters.UPPER_FUNNEL_ROI_PERCENTAGE,
  MROIGridFilters.UPPER_FUNNEL_YIELD_AMOUNT,
];

const MID_FUNNEL_FILTERS = [
  MROIGridFilters.MID_FUNNEL_SPEND_PERCENTAGE,
  MROIGridFilters.MID_FUNNEL_REVENUE_PERCENTAGE,
  MROIGridFilters.MID_FUNNEL_SPEND_AMOUNT,
  MROIGridFilters.MID_FUNNEL_REVENUE_AMOUNT,
  MROIGridFilters.MID_FUNNEL_ROI_PERCENTAGE,
  MROIGridFilters.MID_FUNNEL_YIELD_AMOUNT,
];

const LOWER_FUNNEL_FILTERS = [
  MROIGridFilters.LOWER_FUNNEL_SPEND_PERCENTAGE,
  MROIGridFilters.LOWER_FUNNEL_REVENUE_PERCENTAGE,
  MROIGridFilters.LOWER_FUNNEL_SPEND_AMOUNT,
  MROIGridFilters.LOWER_FUNNEL_REVENUE_AMOUNT,
  MROIGridFilters.LOWER_FUNNEL_ROI_PERCENTAGE,
  MROIGridFilters.LOWER_FUNNEL_YIELD_AMOUNT,
];

const TOTAL_BASIC_FILTERS = [
  MROIGridFilters.TOTAL_SPEND_PERCENTAGE,
  MROIGridFilters.TOTAL_REVENUE_PERCENTAGE,
  MROIGridFilters.TOTAL_ROI_PERCENTAGE,
  MROIGridFilters.TOTAL_SPEND_AMOUNT,
  MROIGridFilters.TOTAL_REVENUE_AMOUNT,
  MROIGridFilters.TOTAL_YIELD_AMOUNT,
];

const AD_STOCK_FILTERS = [MROIGridFilters.AD_STOCK_PEAK, MROIGridFilters.AD_STOCK_LENGTH];

const defaultFilterMap: Partial<FilterMap> = {
  [Layer.Portfolio]: {
    [FunnelStage.All]: [...TOTAL_BASIC_FILTERS, ...AD_STOCK_FILTERS],
  },
  [Layer.Brand]: {
    [FunnelStage.All]: [...TOTAL_BASIC_FILTERS, ...AD_STOCK_FILTERS],
  },
  [Layer.Category]: {
    [FunnelStage.All]: [...TOTAL_BASIC_FILTERS, ...AD_STOCK_FILTERS],
  },
  [Layer.Unknown]: {
    [FunnelStage.All]: [],
  },
};

const filterMap: FilterMap = {
  [Layer.Portfolio]: {
    [FunnelStage.All]: [
      ...TOTAL_BASIC_FILTERS,
      ...UPPER_FUNNEL_FILTERS,
      ...MID_FUNNEL_FILTERS,
      ...LOWER_FUNNEL_FILTERS,
      ...AD_STOCK_FILTERS,
    ],
    [FunnelStage.Portfolio]: [...UPPER_FUNNEL_FILTERS],
    [FunnelStage.Brand]: [...UPPER_FUNNEL_FILTERS],
    [FunnelStage.Category]: [...MID_FUNNEL_FILTERS],
    [FunnelStage.Product]: [...LOWER_FUNNEL_FILTERS],
  },
  [Layer.Brand]: {
    [FunnelStage.All]: [
      ...TOTAL_BASIC_FILTERS,
      ...UPPER_FUNNEL_FILTERS,
      ...MID_FUNNEL_FILTERS,
      ...LOWER_FUNNEL_FILTERS,
      ...AD_STOCK_FILTERS,
    ],
    [FunnelStage.Portfolio]: [
      ...UPPER_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
    [FunnelStage.Brand]: [
      ...UPPER_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
    [FunnelStage.Category]: [
      ...MID_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
    [FunnelStage.Product]: [
      ...LOWER_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
  },
  [Layer.Category]: {
    [FunnelStage.All]: [
      ...TOTAL_BASIC_FILTERS,
      ...MID_FUNNEL_FILTERS,
      ...LOWER_FUNNEL_FILTERS,
      ...AD_STOCK_FILTERS,
    ],
    [FunnelStage.Category]: [
      ...MID_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
    [FunnelStage.Product]: [
      ...LOWER_FUNNEL_FILTERS,
      MROIGridFilters.AD_STOCK_PEAK,
      MROIGridFilters.AD_STOCK_LENGTH,
    ],
  },
  [Layer.Product]: {
    [FunnelStage.All]: [
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
      MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
      MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
      MROIGridFilters.LOWER_FUNNEL_SPEND_AMOUNT,
      MROIGridFilters.LOWER_FUNNEL_SPEND_PERCENTAGE,
      MROIGridFilters.LOWER_FUNNEL_REVENUE_AMOUNT,
      MROIGridFilters.LOWER_FUNNEL_REVENUE_PERCENTAGE,
      MROIGridFilters.TOTAL_REVENUE_AMOUNT,
      MROIGridFilters.TOTAL_ROI_PERCENTAGE,
      ...AD_STOCK_FILTERS,
    ],
    [FunnelStage.Portfolio]: [
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
    ],
    [FunnelStage.Brand]: [
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
      MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
    ],
    [FunnelStage.Category]: [
      MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
      MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
    ],
    [FunnelStage.Product]: [...LOWER_FUNNEL_FILTERS],
  },
  [Layer.Unknown]: {
    // Unknown layer doesn't have any specific filters
    [FunnelStage.All]: [],
    [FunnelStage.Brand]: [],
    [FunnelStage.Category]: [],
    [FunnelStage.Product]: [],
  },
};

/**
 * Returns the available filters for a given layer and funnel stage
 * This is a subset of the filters that are shown in the grid
 */
const determineAvailableFilters = (layer: Layer, funnel: FunnelStage | null): MROIGridFilters[] => {
  if (!funnel) {
    return [];
  }
  return filterMap[layer]?.[funnel] || [];
};

/**
 * Returns the default filters that should be shown for a given layer and funnel stage
 * These are a subset of the available filters that are shown by default
 */
const determineDefaultFilters = (layer: Layer, funnel: FunnelStage | null): MROIGridFilters[] => {
  if (!funnel) {
    return [];
  }

  return defaultFilterMap[layer]?.[funnel] || determineAvailableFilters(layer, funnel);
};

export { determineAvailableFilters, determineDefaultFilters, FunnelStage };
